/**
 * The person kind's metadata shape.
 *
 * Out of `storage/schema/people.ts`, which is now the media kinds' alone. `docs/target-structure.md:84`
 * gives `entries` the metadata shape, the guard and the predicates.
 */

import type { Entry } from '@/document'

/**
 * Contact frequency values for nudge thresholds.
 */
export type ContactFrequency =
  | 'weekly'
  | 'fortnightly'
  | 'monthly'
  | 'quarterly'

/**
 * Contact frequency options with labels and day thresholds.
 */
export const CONTACT_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly', days: 7 },
  { value: 'fortnightly', label: 'Fortnightly', days: 14 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'quarterly', label: 'Quarterly', days: 90 },
] as const

/**
 * Metadata for person entries.
 */
export interface PersonMeta {
  /** Person category (family, friend, work) */
  category?: string
  /** Birthday (ISO string) */
  birthday?: string
  /**
   * Desired contact frequency for nudge reminders.
   *
   * The writer clears this with `delete`, so a cleared field is absent (#381). `| null` stays in the
   * type because documents written before that change hold an explicit `null` indefinitely, and
   * `backup-import-button.tsx` re-injects one from any older backup file. Readers must tolerate both
   * — do not "tidy" the `!= null` guard at `usePeopleNudges.ts:33` into a truthiness check on the
   * grounds that the writer no longer produces null.
   */
  contactFrequency?: ContactFrequency | null
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown
}

/**
 * Person entry with typed metadata.
 */
export interface PersonEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'person'
  metadata: PersonMeta
}

/** A person entry, with its metadata typed. */
export interface PersonEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'person'
  metadata: PersonMeta
}
